import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { syncNow } from '@/lib/sync';
import { toast } from '@/stores/toastStore';
import { useRows } from '@/hooks/useLive';
import { dateTime, humanise, isoDate, num } from '@/lib/format';
import { useSyncStore } from '@/stores/syncStore';
import { maxPerShift, meterName, meterSuffix, type MeterUnit } from '@/lib/meter';
import { Button } from '@/components/ui/Button';
import { DateTimeInput, NumberInput, TextArea } from '@/components/ui/Field';
import type { DailyDiary, Machine, Shift } from '@/types/models';

/* ------------------------------------------------------------------ */
/* Changing a machine's meter type (migration 0034)                    */
/* ------------------------------------------------------------------ */

interface Impact {
  meter_unit: MeterUnit;
  diaries: number;
  /** Diaries taken in the machine's current unit (older ones may predate a new meter). */
  in_unit?: number;
  approved: number;
  over_24: number;
  services: number;
  breakdowns: number;
  liners_fitted: number;
}

type Mode = 'relabel' | 'new_meter';

const UNIT_WORD: Record<MeterUnit, string> = { hours: 'hours', km: 'kilometres' };

function errText(err: unknown, fallback: string): string {
  return (err as { message?: string })?.message ?? fallback;
}

/**
 * Shown in the machine's record when the Meter dropdown is changed on an
 * existing machine. The change is not part of the normal Save: it asks how
 * to treat the readings already captured, then applies in one step.
 */
export function MeterChangePanel({
  machine, to, onCancel, onDone,
}: {
  machine: Machine;
  to: MeterUnit;
  onCancel: () => void;
  onDone: () => void | Promise<void>;
}) {
  const from: MeterUnit = machine.hour_meter_unit === 'km' ? 'km' : 'hours';
  const [impact, setImpact] = useState<Impact | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode | null>(null);
  const [reading, setReading] = useState<number | null>(null);
  const [interval, setInterval] = useState<number | null>(null);
  const [next, setNext] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      const { data, error: err } = await supabase.rpc('meter_unit_impact', { p_machine: machine.id });
      if (!live) return;
      if (err) setLoadErr(err.message);
      else setImpact(data as Impact);
    })();
    return () => { live = false; };
  }, [machine.id]);

  const relabelBlocked = to === 'hours' && (impact?.over_24 ?? 0) > 0;
  const newMeterBlocked = (impact?.liners_fitted ?? 0) > 0;
  const kmToHours = from === 'km' && to === 'hours';

  const derivedNext = mode === 'new_meter' && reading != null
    ? reading + Number(interval ?? machine.service_interval_hours ?? 0)
    : null;

  const canApply = !!mode && !busy && !!impact
    && reason.trim().length > 0
    && !(mode === 'relabel' && relabelBlocked)
    && !(mode === 'new_meter' && (newMeterBlocked || reading == null || reading < 0));

  async function apply() {
    if (!mode) return;
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await supabase.rpc('change_meter_unit', {
        p_machine: machine.id,
        p_unit: to,
        p_mode: mode,
        p_new_reading: mode === 'new_meter' ? reading : null,
        p_interval: interval,
        p_next_service: next,
        p_reason: reason.trim(),
      });
      if (err) throw err;
      await syncNow();
      toast.success(`${machine.fleet_no} now reads in ${UNIT_WORD[to]}`);
      await onDone();
    } catch (err) {
      setError(errText(err, 'The meter type was not changed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="meter-change field--full" data-testid="meter-change">
      <div className={`issue ${kmToHours ? 'issue--block' : 'issue--warn'}`} style={{ display: 'block' }}>
        <strong>
          {kmToHours
            ? `Warning: ${machine.fleet_no} is being changed from kilometres to hours.`
            : `${machine.fleet_no} is being changed from ${UNIT_WORD[from]} to ${UNIT_WORD[to]}.`}
        </strong>{' '}
        {kmToHours
          ? 'An hour meter allows at most 24 in a shift, and service intervals set in kilometres will be far too long in hours. '
          : 'Service intervals set in hours will be far too short in kilometres. '}
        {impact && impact.in_unit === 0 && impact.diaries > 0
          ? `None of its ${num(impact.diaries)} diaries were recorded in ${UNIT_WORD[from]}: they all predate its current meter.`
          : impact && impact.in_unit != null && impact.in_unit < impact.diaries
          ? `${num(impact.in_unit)} of its ${num(impact.diaries)} diaries were recorded in ${UNIT_WORD[from]}; the rest predate its current meter.`
          : `Every reading already captured for this machine was recorded as ${UNIT_WORD[from]}.`}
      </div>

      {loadErr && <p className="small" style={{ color: 'var(--rag-red)' }}>{loadErr}</p>}
      {impact && (
        <p className="small" style={{ margin: 'var(--sp-2) 0' }}>
          On record: <strong>{num(impact.diaries)}</strong> diar{impact.diaries === 1 ? 'y' : 'ies'}
          {impact.approved > 0 && <> ({num(impact.approved)} approved)</>},{' '}
          {num(impact.services)} service{impact.services === 1 ? '' : 's'},{' '}
          {num(impact.breakdowns)} breakdown{impact.breakdowns === 1 ? '' : 's'}.
          {impact.over_24 > 0 && (
            <> <strong>{num(impact.over_24)}</strong> diar{impact.over_24 === 1 ? 'y covers' : 'ies cover'} more than 24 in one shift.</>
          )}
        </p>
      )}

      <div className="col" style={{ gap: 'var(--sp-2)' }}>
        <label className={`meter-choice${relabelBlocked ? ' meter-choice--off' : ''}`}>
          <input type="radio" name="meter-mode" value="relabel" disabled={relabelBlocked}
            checked={mode === 'relabel'} onChange={() => setMode('relabel')} />
          <span>
            <strong>The readings were in {UNIT_WORD[to]} all along — correct the label</strong>
            <span className="small muted" style={{ display: 'block' }}>
              The numbers stay as they are; the diaries recorded in {UNIT_WORD[from]} are marked as {UNIT_WORD[to]}.
              {relabelBlocked && ' Not possible: some diaries cover more than 24 in a shift. Fix those on the Readings tab first, or fit a new meter.'}
            </span>
          </span>
        </label>
        <label className={`meter-choice${newMeterBlocked ? ' meter-choice--off' : ''}`}>
          <input type="radio" name="meter-mode" value="new_meter" disabled={newMeterBlocked}
            checked={mode === 'new_meter'} onChange={() => setMode('new_meter')} />
          <span>
            <strong>A new {meterName(to)} from today</strong>
            <span className="small muted" style={{ display: 'block' }}>
              History keeps its {UNIT_WORD[from]}. The reading and the service window start again in {UNIT_WORD[to]}.
              {newMeterBlocked && ' Not possible while liners are fitted — record their removal first.'}
            </span>
          </span>
        </label>
      </div>

      {mode && (
        <div className="form-grid" style={{ marginTop: 'var(--sp-3)' }}>
          {mode === 'new_meter' && (
            <NumberInput id="mc-reading" label={`Today's ${meterName(to)} reading`} required
              suffix={meterSuffix(to)} min={0} step={to === 'km' ? 1 : 0.1}
              value={reading} onChange={setReading} />
          )}
          <NumberInput id="mc-interval" label="Service interval" suffix={meterSuffix(to)} min={1}
            step={to === 'km' ? 1000 : 50}
            value={interval} onChange={setInterval}
            help={`Blank keeps ${num(machine.service_interval_hours ?? 0, 0)} — check it suits ${UNIT_WORD[to]}.`} />
          <NumberInput id="mc-next" label="Next service at" suffix={meterSuffix(to)} min={0} step={1}
            value={next} onChange={setNext}
            help={derivedNext != null ? `Blank uses ${num(derivedNext, 0)} ${meterSuffix(to)}` : undefined} />
          <div className="field--full">
            <TextArea id="mc-reason" label="Reason" required value={reason} onChange={setReason}
              help="Kept with the machine's meter adjustments." />
          </div>
        </div>
      )}

      {error && <div className="issue issue--block" style={{ display: 'block', marginTop: 'var(--sp-2)' }}>{error}</div>}

      <div className="row" style={{ gap: 'var(--sp-2)', marginTop: 'var(--sp-3)', justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Keep {UNIT_WORD[from]}</Button>
        <Button onClick={apply} loading={busy} disabled={!canApply}>
          Change to {UNIT_WORD[to]}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Correcting readings on submitted and approved diaries               */
/* ------------------------------------------------------------------ */

interface Edit { opening: number | null; closing: number | null; date: string }

export function MeterReadings({ machine }: { machine: Machine }) {
  const diaries = useRows<DailyDiary>('daily_diary');
  const shifts = useRows<Shift>('shifts');
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shiftOrder = useMemo(() => {
    const m = new Map<string, Shift>();
    for (const s of shifts ?? []) m.set(s.id, s);
    return m;
  }, [shifts]);

  // Newest first, with the previous (older) diary's closing alongside to
  // show where a reading does not follow on.
  const rows = useMemo(() => {
    const mine = (diaries ?? [])
      .filter((d) => d.machine_id === machine.id)
      .sort((a, b) =>
        b.diary_date.localeCompare(a.diary_date)
        || (shiftOrder.get(b.shift_id)?.sort_order ?? 0) - (shiftOrder.get(a.shift_id)?.sort_order ?? 0));
    return mine.map((d, i) => ({ d, prev: mine[i + 1] ?? null }));
  }, [diaries, machine.id, shiftOrder]);

  const visible = showAll ? rows : rows.slice(0, 60);

  function val(d: DailyDiary, k: 'opening' | 'closing'): number | null {
    const e = edits[d.id];
    if (e) return e[k];
    return Number(k === 'opening' ? d.opening_hour_meter : d.closing_hour_meter);
  }

  /** The date as corrected, or as stored. */
  const dateOf = (d: DailyDiary): string => edits[d.id]?.date ?? d.diary_date;

  function setVal(d: DailyDiary, patch: Partial<Edit>) {
    setEdits((all) => {
      const cur = all[d.id] ?? {
        opening: Number(d.opening_hour_meter), closing: Number(d.closing_hour_meter), date: d.diary_date,
      };
      const nextEdit = { ...cur, ...patch };
      const same = nextEdit.opening === Number(d.opening_hour_meter)
        && nextEdit.closing === Number(d.closing_hour_meter)
        && nextEdit.date === d.diary_date;
      const copy = { ...all };
      if (same) delete copy[d.id]; else copy[d.id] = nextEdit;
      return copy;
    });
  }

  const today = isoDate();

  function problem(d: DailyDiary): string | null {
    const o = val(d, 'opening');
    const c = val(d, 'closing');
    const day = dateOf(d);
    if (!day) return 'A date is needed';
    if (day > today) return 'The date is in the future';
    // Two diaries of one machine on the same day and shift (0047 refuses it too).
    if (rows.some(({ d: x }) => x.id !== d.id && x.shift_id === d.shift_id && dateOf(x) === day)) {
      return 'Another diary has this date and shift';
    }
    if (o == null || c == null) return 'Both readings are needed';
    if (c < o) return 'Closing is below opening';
    const unit: MeterUnit = d.meter_unit === 'km' ? 'km' : 'hours';
    if (c - o > maxPerShift(unit)) return `More than ${maxPerShift(unit)} ${meterSuffix(unit)} in one shift`;
    return null;
  }

  const changed = Object.keys(edits);
  const invalid = rows.filter(({ d }) => edits[d.id] && problem(d)).length;

  async function saveAll() {
    setBusy(true);
    setError(null);
    try {
      const p_rows = changed.map((id) => ({
        id, opening: edits[id].opening, closing: edits[id].closing, diary_date: edits[id].date,
      }));
      const { data, error: err } = await supabase.rpc('correct_diary_readings', {
        p_machine: machine.id, p_rows,
      });
      if (err) throw err;
      const n = Number((data as { corrected?: number })?.corrected ?? 0);
      await syncNow();
      setEdits({});
      toast.success(`${n} diar${n === 1 ? 'y' : 'ies'} corrected`);
    } catch (err) {
      setError(errText(err, 'The corrections were not saved'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="meter-readings">
      <p className="small muted" style={{ marginTop: 0 }}>
        Correct the date and the opening and closing readings on this machine&apos;s diaries — submitted
        and approved ones included. Engine hours or distance are recalculated, the machine&apos;s current
        reading follows if its latest diary changes, and every change is kept in the audit trail.
      </p>

      {rows.length === 0 ? (
        <p className="muted">No diaries captured for {machine.fleet_no} yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table readings-table">
            <thead>
              <tr>
                <th>Date</th><th>Shift</th><th>Unit</th>
                <th style={{ textAlign: 'right' }}>Opening</th>
                <th style={{ textAlign: 'right' }}>Closing</th>
                <th style={{ textAlign: 'right' }}>Difference</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(({ d, prev }) => {
                const o = val(d, 'opening');
                const c = val(d, 'closing');
                const unit: MeterUnit = d.meter_unit === 'km' ? 'km' : 'hours';
                const p = problem(d);
                const prevClose = prev && (prev.meter_unit ?? 'hours') === unit
                  ? (edits[prev.id]?.closing ?? Number(prev.closing_hour_meter)) : null;
                // 0061: the first diary on a new meter does not follow on from the old one.
                const fitted = localDay(machine.meter_replaced_at);
                const newMeter = !!fitted && !!prev && prev.diary_date < fitted && d.diary_date >= fitted;
                const gap = !newMeter && prevClose != null && o != null && Math.abs(o - prevClose) > 0.05;
                return (
                  <tr key={d.id} className={edits[d.id] ? 'row--edited' : undefined}>
                    <td>
                      <input type="date" className="tabular readings-date" max={today} required
                        aria-label={`Date of the ${d.diary_date} diary`}
                        value={dateOf(d)} onChange={(e) => setVal(d, { date: e.target.value })} />
                    </td>
                    <td>{shiftOrder.get(d.shift_id)?.code ?? ''}</td>
                    <td>{meterSuffix(unit)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <input type="number" className="tabular readings-input" step="any" min={0}
                        aria-label={`Opening ${d.diary_date}`}
                        value={o ?? ''} onChange={(e) => setVal(d, { opening: e.target.value === '' ? null : Number(e.target.value) })} />
                      {newMeter && (
                        <span className="small" style={{ display: 'block', color: 'var(--accent)' }}>
                          new meter (old ended {num(machine.meter_replaced_old ?? null, unit === 'km' ? 0 : 1)})
                        </span>
                      )}
                      {gap && (
                        <span className="small" style={{ display: 'block', color: 'var(--rag-amber)' }}
                          title="The previous diary closed at a different reading">
                          prev closed {num(prevClose!, unit === 'km' ? 0 : 1)}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input type="number" className="tabular readings-input" step="any" min={0}
                        aria-label={`Closing ${d.diary_date}`}
                        value={c ?? ''} onChange={(e) => setVal(d, { closing: e.target.value === '' ? null : Number(e.target.value) })} />
                    </td>
                    <td style={{ textAlign: 'right' }} className="tabular">
                      {o != null && c != null ? `${num(c - o, unit === 'km' ? 0 : 1)} ${meterSuffix(unit)}` : '—'}
                      {p && <span className="small" style={{ display: 'block', color: 'var(--rag-red)' }}>{p}</span>}
                    </td>
                    <td>{humanise(d.status)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 60 && !showAll && (
        <Button variant="secondary" onClick={() => setShowAll(true)}>
          Show all {num(rows.length)} diaries
        </Button>
      )}

      {error && <div className="issue issue--block" style={{ display: 'block', marginTop: 'var(--sp-2)' }}>{error}</div>}

      <div className="row" style={{ gap: 'var(--sp-2)', marginTop: 'var(--sp-3)', justifyContent: 'flex-end', alignItems: 'center' }}>
        {changed.length > 0 && (
          <span className="small muted">
            {changed.length} diar{changed.length === 1 ? 'y' : 'ies'} changed
            {invalid > 0 && ` · ${invalid} to fix`}
          </span>
        )}
        <Button variant="secondary" onClick={() => setEdits({})} disabled={busy || changed.length === 0}>
          Undo changes
        </Button>
        <Button onClick={saveAll} loading={busy} disabled={changed.length === 0 || invalid > 0}>
          Save corrections
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* A replaced meter (migration 0061)                                   */
/* ------------------------------------------------------------------ */

interface Replacement {
  id: string; replaced_at: string; meter_unit: MeterUnit; old_reading: number; new_reading: number;
  next_service_before: number | null; next_service_after: number | null; reason: string; created_at: string;
}

/** The Windhoek calendar date of a moment, for comparing with diary/check dates. */
function localDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Windhoek' }).format(new Date(iso));
}

/**
 * A new hour meter or odometer of the same kind. The old meter's last
 * reading and the new one's first are recorded; the service count is held
 * (next service moves by the same difference), and readings after it follow
 * on from the old meter. Managers and administrators, online.
 */
export function MeterReplacePanel({ machine, onDone }: { machine: Machine; onDone: () => void | Promise<void> }) {
  const online = useSyncStore((s) => s.online);
  const unit: MeterUnit = machine.hour_meter_unit === 'km' ? 'km' : 'hours';
  const sfx = meterSuffix(unit);
  const dec = unit === 'km' ? 0 : 1;
  const [list, setList] = useState<Replacement[] | null>(null);
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<string | null>(() => new Date().toISOString());
  const [oldR, setOldR] = useState<number | null>(Number(machine.current_hour_meter ?? 0));
  const [newR, setNewR] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!online) return;
    let live = true;
    void (async () => {
      const { data, error: err } = await supabase.from('meter_replacements')
        .select('id, replaced_at, meter_unit, old_reading, new_reading, next_service_before, next_service_after, reason, created_at')
        .eq('machine_id', machine.id).is('deleted_at', null).order('replaced_at', { ascending: false });
      if (!live) return;
      if (err) setError(err.message); else setList((data ?? []) as Replacement[]);
    })();
    return () => { live = false; };
  }, [machine.id, online, tick]);

  const next = machine.next_service_hours ?? (machine.last_service_hours != null && machine.service_interval_hours != null
    ? Number(machine.last_service_hours) + Number(machine.service_interval_hours) : null);
  const toGo = next != null && oldR != null ? Number(next) - oldR : null;
  const nextAfter = toGo != null && newR != null ? newR + toGo : null;
  const canApply = online && !busy && at != null && oldR != null && oldR >= 0 && newR != null && newR >= 0
    && reason.trim().length > 0 && oldR >= Number(machine.current_hour_meter ?? 0) - 0.001;

  async function apply() {
    setBusy(true); setError(null);
    try {
      const { data, error: err } = await supabase.rpc('replace_meter', {
        p_machine: machine.id, p_at: at, p_old_reading: oldR, p_new_reading: newR, p_reason: reason.trim(),
      });
      if (err) throw err;
      await syncNow();
      const d = data as { to_go?: number | null; next_service_after?: number | null };
      toast.success(d?.next_service_after != null
        ? `New meter recorded. Next service at ${num(d.next_service_after, 0)} ${sfx} (${num(d.to_go, dec)} ${sfx} to go).`
        : 'New meter recorded.');
      setOpen(false); setNewR(null); setReason(''); setTick((t) => t + 1);
      await onDone();
    } catch (err) {
      setError(errText(err, 'The new meter was not recorded'));
    } finally { setBusy(false); }
  }

  async function undo(r: Replacement) {
    if (!window.confirm(`Take back the meter replacement of ${dateTime(r.replaced_at)}? The readings since go back on the old meter, and the next service with them.`)) return;
    setBusy(true); setError(null);
    try {
      const { error: err } = await supabase.rpc('undo_meter_replacement', { p_id: r.id });
      if (err) throw err;
      await syncNow();
      toast.success('Meter replacement taken back');
      setTick((t) => t + 1);
      await onDone();
    } catch (err) {
      setError(errText(err, 'The replacement was not taken back'));
    } finally { setBusy(false); }
  }

  return (
    <div className="meter-replace" data-testid="meter-replace" style={{ marginBottom: 'var(--sp-4)' }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
        <strong>{unit === 'km' ? 'Odometer' : 'Hour meter'} replaced</strong>
        {!open && (
          <Button variant="secondary" onClick={() => setOpen(true)} disabled={!online}>
            Record a new {meterName(unit)}
          </Button>
        )}
      </div>
      {!online && <p className="small muted">Needs a connection.</p>}

      {open && (
        <div className="form-grid" style={{ marginTop: 'var(--sp-2)' }}>
          <DateTimeInput id="mr-at" label="Fitted at" required value={at} onChange={setAt} />
          <span />
          <NumberInput id="mr-old" label={`Old meter's last reading`} required suffix={sfx} min={0} step={unit === 'km' ? 1 : 0.1}
            value={oldR} onChange={setOldR}
            help={`The register shows ${num(machine.current_hour_meter, dec)} ${sfx}.`}
            error={oldR != null && oldR < Number(machine.current_hour_meter ?? 0) - 0.001
              ? `Not below ${num(machine.current_hour_meter, dec)} ${sfx}` : undefined} />
          <NumberInput id="mr-new" label={`New meter's reading when fitted`} required suffix={sfx} min={0} step={unit === 'km' ? 1 : 0.1}
            value={newR} onChange={setNewR} help="Usually 0, or what the new meter showed." />
          <div className="field--full">
            {next != null ? (
              <div className="issue issue--warn" style={{ display: 'block' }} data-testid="meter-replace-preview">
                Next service: <strong>{num(next, 0)} {sfx}</strong> on the old meter
                {toGo != null && <> — {num(toGo, dec)} {sfx} to go</>}
                {nextAfter != null && <> → <strong>{num(nextAfter, 0)} {sfx}</strong> on the new meter, the same {num(toGo, dec)} {sfx} to go.</>}
              </div>
            ) : <p className="small muted">No service interval is set for {machine.fleet_no}.</p>}
          </div>
          <div className="field--full">
            <TextArea id="mr-reason" label="Reason" required value={reason} onChange={setReason}
              help="E.g. hour meter failed, instrument cluster replaced. Kept with the meter adjustments." />
          </div>
          <div className="field--full row" style={{ gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => { setOpen(false); setError(null); }} disabled={busy}>Cancel</Button>
            <Button onClick={apply} loading={busy} disabled={!canApply}>Record new meter</Button>
          </div>
        </div>
      )}

      {error && <div className="issue issue--block" style={{ display: 'block', marginTop: 'var(--sp-2)' }}>{error}</div>}

      {list && list.length > 0 && (
        <table className="table" style={{ marginTop: 'var(--sp-2)' }}>
          <thead><tr><th>Fitted</th><th style={{ textAlign: 'right' }}>Old meter ended</th><th style={{ textAlign: 'right' }}>New meter from</th>
            <th style={{ textAlign: 'right' }}>Next service</th><th>Reason</th><th /></tr></thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={r.id}>
                <td className="tabular">{dateTime(r.replaced_at)}</td>
                <td className="tabular" style={{ textAlign: 'right' }}>{num(r.old_reading, dec)} {sfx}</td>
                <td className="tabular" style={{ textAlign: 'right' }}>{num(r.new_reading, dec)} {sfx}</td>
                <td className="tabular" style={{ textAlign: 'right' }}>
                  {r.next_service_before != null ? `${num(r.next_service_before, 0)} → ${num(r.next_service_after, 0)} ${sfx}` : '—'}
                </td>
                <td className="small">{r.reason}</td>
                <td>{i === 0 && <Button variant="secondary" onClick={() => void undo(r)} disabled={busy || !online}>Take back</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {list && list.length === 0 && !open && <p className="small muted" style={{ marginBottom: 0 }}>No meter replaced on record.</p>}
    </div>
  );
}
